/* ─── What happens to the ten hubs that exist today ─────────────────────── */

export interface HubDecision {
  current: string
  views: number | null
  action: 'Keep' | 'Widen' | 'Fold'
  becomes: string
  why: string
}

export const hubPlan: HubDecision[] = [
  {
    current: '/family-events-santa-barbara',
    views: 260,
    action: 'Keep',
    becomes: 'Events',
    why: '2:47 on page — your single best asset. Gets its own landing page and a real calendar behind it.',
  },
  {
    current: '/family-activities-in-santa-barbara',
    views: 249,
    action: 'Widen',
    becomes: 'Things to Do',
    why: 'Absorbs Attractions and Libraries as categories. Becomes the biggest hub on the site.',
  },
  {
    current: '/kids-classes-sports-santa-barbara',
    views: 168,
    action: 'Keep',
    becomes: 'Classes & Sports',
    why: '31% bounce, 17% exit — the healthiest page you have. Copy this pattern everywhere else.',
  },
  {
    current: '/santa-barbara-parks-playgrounds-kids',
    views: 156,
    action: 'Keep',
    becomes: 'Parks & Playgrounds',
    why: 'Strong standalone search demand. Currently lists two parks — needs 25+ before it earns its traffic.',
  },
  {
    current: '/parenting-resources-santa-barbara',
    views: 110,
    action: 'Widen',
    becomes: 'Parent Resources',
    why: 'Becomes the parent for all support content, including the two hubs folding into it.',
  },
  {
    current: '/santa-barbara-family-attractions',
    views: 102,
    action: 'Fold',
    becomes: '→ Things to Do',
    why: '89.5% bounce, the worst on the site. Too thin to survive alone. 301 it.',
  },
  {
    current: '/kids-shopping-santa-barbara',
    views: 99,
    action: 'Widen',
    becomes: 'Shops & Eats',
    why: 'Keep the URL, widen the scope to include kid-friendly restaurants — a much bigger search than toy stores.',
  },
  {
    current: '/santa-barbara-fitness-childcare',
    views: 82,
    action: 'Fold',
    becomes: '→ Parent Resources',
    why: '0:14 on page. It is one good article, not a category. 301 it to a child page.',
  },
  {
    current: '/preschools-daycares-santa-barbara',
    views: null,
    action: 'Keep',
    becomes: 'Preschools & Childcare',
    why: 'Highest revenue intent on the whole site. Schools will pay for placement here.',
  },
  {
    current: '/santa-barbara-kids-libraries',
    views: null,
    action: 'Fold',
    becomes: '→ Things to Do',
    why: 'Real content, but it is one page of value. Becomes a child page under Things to Do.',
  },
  {
    current: '/kids-wellness-santa-barbara',
    views: null,
    action: 'Fold',
    becomes: '→ Parent Resources',
    why: 'Pediatricians and therapists belong with the other support content parents search for together.',
  },
]

export const redirects = [
  ['/santa-barbara-family-attractions', '/family-activities-in-santa-barbara'],
  ['/santa-barbara-kids-libraries', '/family-activities-in-santa-barbara/libraries-with-kids'],
  ['/santa-barbara-fitness-childcare', '/parenting-resources-santa-barbara/fitness-with-childcare'],
  ['/kids-wellness-santa-barbara', '/parenting-resources-santa-barbara/kids-wellness'],
]

/* ─── How much page each thing deserves ─────────────────────────────────── */

export interface Tier {
  tier: string
  name: string
  effort: string
  volume: string
  test: string
  contains: string[]
  tone: 'olive' | 'opal' | 'peach'
}

export const tiers: Tier[] = [
  {
    tier: '01',
    name: 'Full place page',
    effort: '45–90 min each',
    volume: '~60 pages, ever',
    test: 'Someone searches it by name, you have a real opinion about it, and a business could plausibly sponsor it. If all three are true, build the page.',
    contains: [
      'Facts panel: ages, cost, hours, parking, registration',
      '400–800 words in your own voice',
      'Two or three quotes from local parents',
      'Your own photos',
      'Three FAQs',
      'Four onward links',
    ],
    tone: 'olive',
  },
  {
    tier: '02',
    name: 'Rich row on the hub',
    effort: '10 min each',
    volume: '~150 entries',
    test: 'Worth knowing about, but you have nothing to say beyond the facts. Most places live here permanently and that is fine.',
    contains: [
      'Name, area, ages, cost',
      'One or two honest sentences',
      'Three deciding facts (shade, restrooms, parking)',
      'A link straight to their site',
    ],
    tone: 'opal',
  },
  {
    tier: '03',
    name: 'Directory line',
    effort: '2 min each',
    volume: 'Unlimited',
    test: 'Completeness. A plain list at the bottom of the hub so the page is genuinely comprehensive and nobody leaves to check another site.',
    contains: ['Name, neighborhood, outbound link'],
    tone: 'peach',
  },
]

export const promotionRule = {
  title: 'Let demand decide, not guilt',
  body: 'Start everything at Tier 2. Promote to Tier 1 when one of three things happens: the row gets clicked a lot, a parent emails you a question about it, or the business asks about advertising. You will never build 200 full pages, and you do not need to — 60 good ones carry the whole site.',
}

/* ─── The complete page inventory, in build order ───────────────────────── */

export interface Phase {
  phase: string
  window: string
  goal: string
  pages: { label: string; note: string; count: string }[]
}

export const phases: Phase[] = [
  {
    phase: 'Phase one',
    window: 'Weeks 1–3',
    goal: 'Fix the structure before adding a single new word. This is where the traffic you already have starts converting.',
    pages: [
      { label: 'Home, rebuilt as a switchboard', note: 'All seven hubs visible, search on top', count: '1 page' },
      { label: 'Seven hub pages, rebuilt as directories', note: 'Short intro, filters, scannable rows', count: '7 pages' },
      { label: 'Four 301 redirects', note: 'Attractions, Libraries, Fitness, Wellness', count: '0 pages' },
      { label: 'Events landing page + calendar embed', note: 'On your domain, with a submit button', count: '1 page' },
      { label: 'Submit an event form', note: 'Feeds the calendar', count: '1 page' },
    ],
  },
  {
    phase: 'Phase two',
    window: 'Weeks 4–10',
    goal: 'Build the twenty pages that will earn the most, and give every hub something worth arriving for.',
    pages: [
      { label: 'Tier 1 attractions', note: 'SB Zoo, MOXI, Sea Center, Botanic Garden, Stearns Wharf', count: '5 pages' },
      { label: 'Tier 1 parks', note: 'Kids’ World, Shipwreck, Stow Grove, Manning, Elings', count: '5 pages' },
      { label: 'Tier 1 preschools', note: 'The five parents ask about most', count: '5 pages' },
      { label: 'Tier 1 resources', note: 'SBCC Parenting, Mamatoto, top swim, top gym w/ childcare', count: '5 pages' },
      { label: 'Tier 2 rows across every hub', note: 'Get each hub to 20+ entries', count: '~140 rows' },
    ],
  },
  {
    phase: 'Phase three',
    window: 'Months 3–5',
    goal: 'Add the evergreen guides that rank, and the seasonal posts that spike.',
    pages: [
      { label: 'Evergreen guides', note: 'Beaches, rainy day, free things, birthday venues, kid-friendly restaurants, summer camps', count: '6 pages' },
      { label: 'Neighborhood guides', note: 'Downtown, Mesa, Montecito, Goleta, Carpinteria', count: '5 pages' },
      { label: 'Annual event pages', note: 'Fiesta, Solstice, tree lightings, pumpkin patches, summer concerts', count: '6–8 pages' },
      { label: 'Age landing pages', note: '“With a toddler”, “with a baby”, “with tweens”', count: '3 pages' },
    ],
  },
  {
    phase: 'Phase four',
    window: 'Month 6 onward',
    goal: 'Turn the traffic into money.',
    pages: [
      { label: 'Advertise with us', note: 'Rates, audience, sponsored listing tiers', count: '1 page' },
      { label: 'Featured business template', note: 'The interview format you already do well', count: 'ongoing' },
      { label: 'The SB Family Edit', note: 'Affiliate shop, organized by occasion', count: '1 page + items' },
      { label: 'Newsletter landing page', note: 'Its own URL so you can link it everywhere', count: '1 page' },
    ],
  },
]

/* ─── Events ────────────────────────────────────────────────────────────── */

export const eventTruths = [
  'Squarespace cannot import events from a spreadsheet. CSV import exists for Products only — not for Events or Blog posts.',
  'Squarespace has no content API, so no Zapier or Make automation can create events for you either.',
  'Squarespace calendar pages do not support recurring events. Every weekly storytime would be entered by hand, one week at a time.',
  'There is no built-in way for the public to submit an event to a Squarespace calendar.',
]

export interface EventOption {
  name: string
  cost: string
  verdict: 'Recommended' | 'Budget option' | 'Limited use' | 'Only if you love spreadsheets'
  pros: string[]
  cons: string[]
}

export const eventOptions: EventOption[] = [
  {
    name: 'Tockify (what you had)',
    cost: '~$20/month',
    verdict: 'Recommended',
    pros: [
      'Public event submission with moderation — the thing you actually need',
      'Recurring events, which Squarespace cannot do at all',
      'Spreadsheet / CSV import for bulk-loading a season at once',
      'Embeds into a Squarespace page and can be styled to match',
    ],
    cons: [
      'The embed itself does not get indexed by Google',
      'A monthly cost against a site not yet earning',
    ],
  },
  {
    name: 'Google Calendar + Google Form',
    cost: 'Free',
    verdict: 'Budget option',
    pros: [
      'Recurring events work properly',
      'A Google Form plus a short Apps Script auto-creates approved events',
      'Spreadsheet is the source of truth, so bulk edits are easy',
    ],
    cons: [
      'Looks like Google Calendar unless you pay for a styling widget',
      'The one-time script setup is genuinely technical',
      'Also not indexed',
    ],
  },
  {
    name: 'Squarespace calendar page',
    cost: 'Included',
    verdict: 'Limited use',
    pros: [
      'Lives on your domain and can rank in search',
      'Matches your design with zero effort',
    ],
    cons: [
      'No recurring events',
      'No public submissions',
      'Every event entered by hand',
    ],
  },
  {
    name: 'Airtable + embedded view',
    cost: '$0–20/month',
    verdict: 'Only if you love spreadsheets',
    pros: [
      'Submission form built in, spreadsheet-native, filterable',
      'You own the data outright',
    ],
    cons: [
      'Does not look like a calendar without extra tooling',
      'Another tool to maintain',
    ],
  },
]

export const eventRecommendation = {
  headline: 'Go back to Tockify — but stop letting it be the whole page',
  body: 'The weekly churn belongs in a tool that does recurring events and public submissions, because Squarespace does neither. But an embed cannot rank, and /family-events-santa-barbara is your best-performing page. So wrap it: a real Squarespace page with your own intro copy, this-weekend picks written by you, the embed in the middle, and a submit button. Then build six to eight standalone pages for the annual events people search by name — Fiesta with kids, Solstice, tree lightings, pumpkin patches. Those rank; the embed just keeps the page useful.',
}

/* ─── The map ───────────────────────────────────────────────────────────── */

export interface SitemapNode {
  label: string
  path: string
  categories: string[]
  children: string[]
}

export const sitemap: SitemapNode[] = [
  {
    label: 'Family events',
    path: '/family-events-santa-barbara',
    categories: ['This weekend', 'Free', 'Seasonal', 'Annual'],
    children: ['Fiesta with kids', 'Summer Solstice', 'Tree lightings', 'Pumpkin patches'],
  },
  {
    label: 'Things to do',
    path: '/family-activities-in-santa-barbara',
    categories: ['Attractions', 'Beaches', 'Libraries', 'Rainy day', 'Free'],
    children: ['Santa Barbara Zoo', 'MOXI', 'Sea Center', 'Libraries with kids'],
  },
  {
    label: 'Parks & playgrounds',
    path: '/santa-barbara-parks-playgrounds-kids',
    categories: ['Playground', 'Green space', 'Splash pad'],
    children: ['Kids’ World', 'Shipwreck Playground', 'Stow Grove Park'],
  },
  {
    label: 'Classes & sports',
    path: '/kids-classes-sports-santa-barbara',
    categories: ['Swim', 'Dance', 'Gymnastics', 'Soccer', 'Music'],
    children: ['Swim lesson guide', 'Toddler dance', 'Youth soccer leagues'],
  },
  {
    label: 'Preschools & childcare',
    path: '/preschools-daycares-santa-barbara',
    categories: ['Preschool', 'Daycare', 'Co-op', 'Nanny share'],
    children: ['Waitlist timing guide', 'Individual school pages'],
  },
  {
    label: 'Parent resources',
    path: '/parenting-resources-santa-barbara',
    categories: ['Support groups', 'Wellness', 'Fitness w/ childcare', 'Therapists'],
    children: ['SBCC Parenting Program', 'Mamatoto', 'Fitness with childcare', 'Kids wellness'],
  },
  {
    label: 'Shops & eats',
    path: '/kids-shopping-santa-barbara',
    categories: ['Toy stores', 'Kids clothes', 'Restaurants', 'Bakeries'],
    children: ['Kid-friendly restaurants', 'Toy stores worth the trip'],
  },
]

export const standalonePages = [
  '/blog',
  '/about',
  '/shop',
  '/newsletter',
  '/submit-an-event',
  '/advertise',
  '/contact',
]

/* ─── Linking + Squarespace ─────────────────────────────────────────────── */

export const linkingRules = [
  {
    rule: 'Every place page links up to its hub',
    why: 'It is the breadcrumb, and it is how Google understands the cluster.',
  },
  {
    rule: 'Every place page links across to 3–4 siblings',
    why: 'Your detail pages exit at 68%. Three good links is most of the fix.',
  },
  {
    rule: 'Every hub links sideways to 3 other hubs',
    why: 'A parent looking at parks is one thought away from classes.',
  },
  {
    rule: 'Seasonal posts always link into an evergreen hub',
    why: 'Spikes should deposit people somewhere permanent.',
  },
  {
    rule: 'Home links to all seven hubs without scrolling far',
    why: '67% of home visitors currently leave without a second page.',
  },
]

export const squarespaceNotes = [
  {
    title: 'One Blog collection per hub',
    body: 'This is the only way Squarespace produces nested URLs like /parenting-resources-santa-barbara/sbcc-parenting-program — which you already have. Pages cannot nest. Tier 1 place pages are posts inside their hub’s collection.',
  },
  {
    title: 'Categories filter, Tags cross-cut',
    body: 'Category = the sub-type inside a hub (Playground, Preschool, Swim). Tag = things that cut across every hub (Free, Indoor, 0–2, Goleta). Summary Blocks filter on either, which is how the filter chips work without custom code.',
  },
  {
    title: 'Tier 2 rows are blocks, not posts',
    body: 'Do not create a post for every Tier 2 entry — that is the trap that makes this feel like endless work. They are list items on the hub page itself. Promoting one to Tier 1 later means creating a post and linking the row to it.',
  },
  {
    title: 'Summary Blocks keep Home current',
    body: 'One Summary Block per hub, filtered to that collection, limited to three or four items. Home updates itself as you publish.',
  },
  {
    title: 'Set the four 301s before you delete anything',
    body: 'Settings → Advanced → URL Mappings. Format is /old-url -> /new-url 301, one per line. Do this first, then unpublish the old pages.',
  },
  {
    title: 'Save the sponsor block as a Saved Section',
    body: 'One reusable block style means every partner placement looks identical and stays clearly labeled, on every page.',
  },
]
